'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { X } from 'lucide-react';
import { useWeatherBar } from '@/context/WeatherBarContext';
import { getSnowRating } from '@/data/weather';
import {
  resolveWeatherLabels,
  type WeatherLabels,
} from '@/components/weather/labels';

interface WeatherData {
  levi: { temperature: number };
  yllas: { temperature: number };
}

interface SnowData {
  levi: { baseDepth: number };
  yllas: { baseDepth: number };
}

export default function WeatherTopBar({ labels }: { labels?: WeatherLabels }) {
  const L = resolveWeatherLabels(labels);
  const { setVisible } = useWeatherBar();
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [snow, setSnow] = useState<SnowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('kettura-topbar-dismissed');
    if (stored === 'true') {
      setDismissed(true);
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;

    Promise.all([
      fetch('/api/weather').then((res) => res.json()),
      fetch('/api/snow').then((res) => res.json()),
    ])
      .then(([weatherJson, snowJson]) => {
        setWeather(weatherJson);
        setSnow(snowJson);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dismissed]);

  const isVisible = !dismissed;

  useEffect(() => {
    setVisible(isVisible);
  }, [isVisible, setVisible]);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('kettura-topbar-dismissed', 'true');
  };

  const avgSnow =
    snow ? Math.round((snow.levi.baseDepth + snow.yllas.baseDepth) / 2) : 0;
  const rating = snow ? getSnowRating(avgSnow) : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-50 w-full h-11 bg-[#9B7B3A] flex items-center justify-center font-sans text-xs text-white"
      >
        <Link
          href="/weather"
          className="flex items-center justify-center w-full h-full pl-3 pr-12 cursor-pointer hover:bg-white/10 transition-colors"
        >
          {loading ? (
            <span className="truncate text-white/90">{L.topbarLoading}</span>
          ) : weather && snow ? (
            <div className="flex min-w-0 items-center justify-center gap-3 sm:gap-4 whitespace-nowrap">
              <span>
                Levi: {Math.round(weather.levi.temperature)}°C<span className="hidden sm:inline"> / {L.topbarSnow} {snow.levi.baseDepth}cm</span>
              </span>
              <span className="hidden sm:inline text-white/50">|</span>
              <span>
                Ylläs: {Math.round(weather.yllas.temperature)}°C<span className="hidden sm:inline"> / {L.topbarSnow} {snow.yllas.baseDepth}cm</span>
              </span>
              {rating && (
                <span className="hidden lg:inline">
                  <span className="hidden sm:inline text-white/50">|</span>
                  <span>
                    {L.topbarSnowIndex} {avgSnow}cm -{' '}
                    <span className="font-semibold">{L.ratings[rating]}</span>
                  </span>
                </span>
              )}
            </div>
          ) : (
            <span className="truncate text-white/90">{L.topbarUnavailable}</span>
          )}
        </Link>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded z-10 text-white/70 hover:text-white transition-colors"
          aria-label={L.topbarDismiss}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
